import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useDataScope } from "@/hooks/useDataScope";
import ClaimsCard from "@/components/ClaimsCard";
import FileClaimDialog from "@/components/FileClaimDialog";
import StatusBadge from "@/components/StatusBadge";
import { formatDate } from "@/lib/format";
import { Search, ShieldAlert, Loader2, X, Layers } from "lucide-react";

export default function Claims() {
  const { param, allMode, accountId } = useDataScope();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [date, setDate] = useState("");
  const [claimOrder, setClaimOrder] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const qs = param ? `?${param}` : "";
    api.get(`/orders${qs}`)
      .then(({ data }) => { if (!cancelled) setOrders(data.orders || []); })
      .catch(() => { if (!cancelled) setOrders([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [param]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return orders.filter((o) => {
      const matchTerm = !term || (o.order_number || "").toLowerCase().includes(term) || (o.customer_name || "").toLowerCase().includes(term);
      const matchDate = !date || (o.order_date || "").slice(0, 10) === date;
      return matchTerm && matchDate;
    });
  }, [orders, q, date]);

  const showResults = q.trim() || date;

  return (
    <div data-testid="claims-page" className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center">
          <ShieldAlert className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>Claims</h1>
          <p className="text-slate-500 mt-0.5">File a claim for a lost or damaged shipment and track it end to end.</p>
        </div>
      </div>

      {allMode ? (
        <div data-testid="claims-all-mode" className="flex items-start gap-3 bg-blue-50 border border-blue-200 text-blue-800 rounded-lg px-4 py-4">
          <Layers className="w-5 h-5 mt-0.5 shrink-0" />
          <p className="text-sm">Claims are per store. Pick a specific store from the account switcher (top-right) to search its orders and view its claims.</p>
        </div>
      ) : (
        <>
          {/* Order search → file a claim */}
          <div data-testid="claim-order-search" className="bg-white border border-slate-200 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <Search className="w-5 h-5 text-slate-400" />
              <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>Find an order to claim</h2>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  data-testid="order-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search by order number or customer…"
                  className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500"
                />
              </div>
              <div className="relative">
                <input
                  data-testid="order-search-date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="px-3 py-2.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500"
                />
              </div>
              {(q || date) && (
                <button data-testid="order-search-clear" onClick={() => { setQ(""); setDate(""); }} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 px-3">
                  <X className="w-4 h-4" /> Clear
                </button>
              )}
            </div>

            <div className="mt-4">
              {loading ? (
                <div className="flex items-center justify-center gap-2 text-slate-400 py-8"><Loader2 className="w-5 h-5 animate-spin" /> Loading orders…</div>
              ) : !showResults ? (
                <p className="text-sm text-slate-400 py-6 text-center">Start typing an order number or pick a date to find the shipment you need to claim.</p>
              ) : filtered.length === 0 ? (
                <p className="text-sm text-slate-400 py-6 text-center">No orders match your search.</p>
              ) : (
                <div className="overflow-x-auto border border-slate-100 rounded-lg">
                  <table className="w-full text-left text-sm" data-testid="order-search-results">
                    <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                      <tr>
                        <th className="py-2 px-3">Order #</th>
                        <th className="py-2 px-3">Date</th>
                        <th className="py-2 px-3">Customer</th>
                        <th className="py-2 px-3">Ship To</th>
                        <th className="py-2 px-3 text-center">Status</th>
                        <th className="py-2 px-3 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.slice(0, 25).map((o) => (
                        <tr key={o.id} data-testid={`search-order-${o.id}`} className="border-t border-slate-100 hover:bg-slate-50/60">
                          <td className="py-2.5 px-3 font-semibold text-slate-900">{o.order_number}</td>
                          <td className="py-2.5 px-3 text-slate-600">{formatDate(o.order_date)}</td>
                          <td className="py-2.5 px-3 text-slate-700">{o.customer_name}</td>
                          <td className="py-2.5 px-3 text-slate-600">{o.ship_to}</td>
                          <td className="py-2.5 px-3 text-center"><StatusBadge status={o.status} /></td>
                          <td className="py-2.5 px-3 text-right">
                            <button
                              data-testid={`claim-order-${o.id}`}
                              onClick={() => setClaimOrder(o)}
                              className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-orange-500 hover:bg-orange-600 rounded-md px-3 py-1.5 transition-colors"
                            >
                              <ShieldAlert className="w-3.5 h-3.5" /> File claim
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filtered.length > 25 && <p className="text-xs text-slate-400 p-3">Showing first 25 of {filtered.length} matches — refine your search to narrow it down.</p>}
                </div>
              )}
            </div>
          </div>

          {/* Existing claims list */}
          <ClaimsCard param={param} accountId={accountId} allMode={allMode} reloadKey={reloadKey} />
        </>
      )}

      {claimOrder && (
        <FileClaimDialog
          order={claimOrder}
          accountId={allMode ? null : accountId}
          onClose={() => setClaimOrder(null)}
          onSubmitted={() => { setClaimOrder(null); setReloadKey((k) => k + 1); }}
        />
      )}
    </div>
  );
}
